import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDateWrittenShort } from '@/lib/dates';
import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Award,
  Calendar,
  Download,
  Share2,
  ExternalLink,
  Loader2,
  FileText,
} from 'lucide-react';
import { useMyCertificates } from '@/api/hooks';
import { useMyTopTierCommunity, useCreatePost } from '@/api/community-hooks';
import { uploadPostImage, getDefaultChannelIdForTier } from '@/api/community';
import type { Certificate } from '@/api/client';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  renderElementToPng,
  downloadBlob,
  copyPngWithTextFallback,
  blobToFile,
  slugify,
} from '@/lib/certificate-export';
import { ShareConfirmDialog } from '@/components/share/ShareConfirmDialog';

export const Route = createFileRoute('/learn/certificates/')({
  component: CertificatesPage,
});

function CertificatesPage() {
  const { data: certificates = [], isLoading } = useMyCertificates();
  const { data: topTier } = useMyTopTierCommunity();
  const createPostMutation = useCreatePost();

  // Per-card busy state for the download spinner.
  const [busyId, setBusyId] = useState<string | null>(null);
  // Share dialog tracks which certificate it's acting on, plus which button is busy.
  const [shareDialogCertId, setShareDialogCertId] = useState<string | null>(null);
  const [shareBusyAction, setShareBusyAction] = useState<'share' | 'copy' | null>(null);
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});

  const registerRef = (id: string) => (el: HTMLElement | null) => {
    cardRefs.current[id] = el;
  };

  const handleDownload = async (certificate: Certificate) => {
    const el = cardRefs.current[certificate.id];
    if (!el) {
      toast.error('Could not find certificate on page');
      return;
    }
    setBusyId(certificate.id);
    try {
      const blob = await renderElementToPng(el);
      downloadBlob(blob, `certificate-${slugify(certificate.title)}-${certificate.certificate_number}.png`);
    } catch (err) {
      console.error('Certificate download failed', err);
      toast.error('Could not generate certificate');
    } finally {
      setBusyId(null);
    }
  };

  const openShareDialog = (certificate: Certificate) => {
    if (!topTier) {
      toast.error('Join a community to share');
      return;
    }
    setShareDialogCertId(certificate.id);
  };

  const activeShareCert = shareDialogCertId
    ? certificates.find((c) => c.id === shareDialogCertId) ?? null
    : null;

  const handleShareToFeed = async () => {
    const certificate = activeShareCert;
    if (!certificate) return;
    const el = cardRefs.current[certificate.id];
    if (!el) {
      toast.error('Could not find certificate on page');
      return;
    }
    if (!topTier) {
      toast.error('Join a community to share');
      return;
    }
    setShareBusyAction('share');
    try {
      const blob = await renderElementToPng(el);
      const filename = `${slugify(certificate.title)}-${certificate.certificate_number}.png`;
      const caption = `🎓 I just completed "${certificate.title}"`;

      const [postImage, defaultChannelId] = await Promise.all([
        uploadPostImage(blobToFile(blob, filename)),
        getDefaultChannelIdForTier(topTier.tier_id),
        copyPngWithTextFallback(blob, certificate.verification_url).catch((err) => {
          console.warn('Clipboard copy failed', err);
        }),
      ]);

      await createPostMutation.mutateAsync({
        tierId: topTier.tier_id,
        content: caption,
        images: [postImage],
        postType: 'post',
        channelId: defaultChannelId ?? undefined,
      });
      toast.success(`Shared to ${topTier.tier_name} · copied to clipboard`);
      setShareDialogCertId(null);
    } catch (err) {
      console.error('Certificate share failed', err);
      toast.error('Could not share certificate');
    } finally {
      setShareBusyAction(null);
    }
  };

  const handleCopyOnly = async () => {
    const certificate = activeShareCert;
    if (!certificate) return;
    const el = cardRefs.current[certificate.id];
    if (!el) {
      toast.error('Could not find certificate on page');
      return;
    }
    setShareBusyAction('copy');
    try {
      const blob = await renderElementToPng(el);
      await copyPngWithTextFallback(blob, certificate.verification_url);
      toast.success('Copied to clipboard');
      setShareDialogCertId(null);
    } catch (err) {
      console.error('Certificate copy failed', err);
      toast.error('Could not copy certificate');
    } finally {
      setShareBusyAction(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link
            to="/learn"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Learning
          </Link>
          <h1 className="text-2xl font-bold text-foreground">My Certificates</h1>
          <p className="text-muted-foreground">
            Certificates earned from completing learning paths and courses
          </p>
        </div>
        <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
          <Award className="w-8 h-8 text-white" />
        </div>
      </div>

      {/* Certificates grid */}
      {certificates.length > 0 ? (
        <TooltipProvider delayDuration={200}>
          <div className="grid md:grid-cols-2 gap-6">
            {certificates.map((cert, idx) => (
              <CertificateCard
                key={cert.id}
                certificate={cert}
                index={idx}
                registerRef={registerRef(cert.id)}
                busy={busyId === cert.id}
                canShare={!!topTier}
                topTierName={topTier?.tier_name}
                onDownload={handleDownload}
                onShare={openShareDialog}
              />
            ))}
          </div>
        </TooltipProvider>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-xl border border-border p-12 text-center"
        >
          <div className="w-20 h-20 mx-auto bg-secondary rounded-full flex items-center justify-center mb-6">
            <Award className="w-10 h-10 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">No Certificates Yet</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Complete learning paths or courses to earn certificates that showcase your achievements.
          </p>
          <Link
            to="/learn"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all"
          >
            Browse Learning Paths
          </Link>
        </motion.div>
      )}

      <ShareConfirmDialog
        open={shareDialogCertId !== null}
        onOpenChange={(next) => {
          if (!next) setShareDialogCertId(null);
        }}
        communityName={topTier?.tier_name}
        busyAction={shareBusyAction}
        onShareToFeed={handleShareToFeed}
        onCopyToClipboard={handleCopyOnly}
      />
    </div>
  );
}

function CertificateCard({
  certificate,
  index,
  registerRef,
  busy,
  canShare,
  topTierName,
  onDownload,
  onShare,
}: {
  certificate: Certificate;
  index: number;
  registerRef: (el: HTMLElement | null) => void;
  busy: boolean;
  canShare: boolean;
  topTierName: string | undefined;
  onDownload: (cert: Certificate) => void;
  onShare: (cert: Certificate) => void;
}) {
  const issuedDate = formatDateWrittenShort(certificate.issued_at);

  const expiresDate = certificate.expires_at
    ? formatDateWrittenShort(certificate.expires_at)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-card rounded-xl border border-border overflow-hidden"
    >
      {/* Everything inside `ref={registerRef}` gets captured when Download / Share is clicked. */}
      <div ref={registerRef}>
        {/* Certificate preview */}
        <div className="h-40 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 relative flex items-center justify-center">
          <div className="absolute inset-0 bg-[url('/certificate-pattern.svg')] opacity-10" />
          <div className="relative text-center text-white">
            <Award className="w-12 h-12 mx-auto mb-2" />
            <p className="text-sm font-medium opacity-90">Certificate of Completion</p>
          </div>
        </div>

        {/* Certificate details */}
        <div className="p-5">
          <h3 className="font-bold text-lg text-foreground mb-1">{certificate.title}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Awarded to {certificate.recipient_name}
          </p>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Issued: {issuedDate}</span>
            </div>
            {expiresDate && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>Expires: {expiresDate}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span>ID: {certificate.certificate_number}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions — outside the ref so they aren't captured in the screenshot */}
      <div className="px-5 pb-5 flex items-center gap-2">
        <button
          onClick={() => onDownload(certificate)}
          disabled={busy}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download
        </button>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onShare(certificate)}
              disabled={busy || !canShare}
              className="p-2 rounded-lg border border-border hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Share to community"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {canShare
              ? `Share to ${topTierName} · copy to clipboard`
              : 'Join a community to share'}
          </TooltipContent>
        </Tooltip>
        {certificate.verification_url && (
          <a
            href={certificate.verification_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            title="Verify certificate"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </motion.div>
  );
}
